import React from 'react';
import * as ReactDOM from 'react-dom';
import { Link, LinkProps } from '@reach/router';

import DirtyFormContext, { DirtyFormStateInterface } from './DirtyFormContext';
import { hasPermission } from '$/utils/hasPermission';
import { useMe } from '$/hooks/useMe';
import { usePermissionModal } from '../Permission';
import Modal from '../ui/Modal';
import Text from '../ui/Text';
import Button from '../ui/Button';

const textStates: {
  [key: string]: {
    title: string;
    content: string;
    showResetBtn: boolean;
    showSaveBtn: boolean;
    showCloseBtn: boolean;
  };
} = {
  unsavedChanges: {
    title: 'Your changes have not been saved!',
    content: `What would you like to do?`,
    showResetBtn: true,
    showSaveBtn: true,
    showCloseBtn: false,
  },
  isSubmitting: {
    title: 'Please wait a moment...',
    content: `We aren't quite finished saving all of your changes.`,
    showResetBtn: false,
    showSaveBtn: false,
    showCloseBtn: true,
  },
};

const UnsavedChangesModal: React.FC<{
  dirtyForm: DirtyFormStateInterface;
  setDirtyForm: (arg: DirtyFormStateInterface | null) => void;
  triggerNavigation: () => void;
}> = ({ dirtyForm, setDirtyForm, triggerNavigation }) => {
  const textState = dirtyForm.isSubmitting ? textStates.isSubmitting : textStates.unsavedChanges;

  return (
    <Modal backdropClassName='z-unsaved-changes'>
      <Modal.Header gray>
        <Modal.Title className='px-0'>{textState.title}</Modal.Title>
        <Modal.Close onClick={(): void => setDirtyForm(null)} />
      </Modal.Header>
      <Modal.Body>
        <Text.body className='text-teal-6 text-lg whitespace-normal'>
          {textState.content}
          <div className='flex flex-row mt-12 justify-between items-center'>
            {textState.showResetBtn && (
              <Button
                type='button'
                onClick={async (): Promise<void> => {
                  try {
                    setDirtyForm(null);
                    await dirtyForm.reset();
                    setTimeout(() => {
                      triggerNavigation();
                    }, 300);
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                Discard my changes
              </Button>
            )}

            {textState.showSaveBtn && (
              <Button.rounded
                className='ml-1_5e px-5 text-base'
                onClick={async (): Promise<void> => {
                  setDirtyForm(null);

                  if (dirtyForm.trigger) {
                    const result = await dirtyForm.trigger();
                    if (!result) {
                      // validation failed
                      return;
                    }
                  }

                  // submit form
                  await dirtyForm.submit();
                  // navigate to next page
                  setTimeout(() => {
                    triggerNavigation();
                  }, 300);
                }}
              >
                <div className='inline-flex items-center'>
                  Save my changes
                  <img className='ml-2' src={require(`$/images/continueArrow.svg`).default} />
                </div>
              </Button.rounded>
            )}

            {textState.showCloseBtn && (
              <Button
                type='button'
                onClick={(): void => {
                  setDirtyForm(null);
                }}
              >
                Close
              </Button>
            )}
          </div>
        </Text.body>
      </Modal.Body>
    </Modal>
  );
};

export const PermissionedLink: React.FC<
  Omit<React.PropsWithoutRef<LinkProps<{}>> & React.RefAttributes<HTMLAnchorElement>, 'onClick'> & {
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    skipDirtyForm?: boolean;
    disabled?: boolean;
  }
> = ({ children, to, onClick, disabled, skipDirtyForm, ...props }) => {
  const me = useMe();

  // handle show permission modal
  const { showPermissionModal } = usePermissionModal();

  // dirty form context
  const { forms } = React.useContext(DirtyFormContext);

  // show/open unsaved changes modal
  const [dirtyForm, setDirtyForm] = React.useState<DirtyFormStateInterface | null>(null);

  // link ref
  const linkRef = React.useRef<HTMLAnchorElement>(null);

  // navigate to clicked link
  const triggerNavigation = (): void => {
    if (linkRef && linkRef.current) {
      linkRef.current.click();
    }
  };

  // get form id that is dirty
  return (
    <>
      <Link
        to={to}
        className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${props.className}`}
        {...props}
        ref={linkRef}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>): Promise<boolean> => {
          if (disabled) {
            e.preventDefault();
            return false;
          }

          if (!hasPermission(to, me)) {
            // check permission
            e.preventDefault();
            showPermissionModal && showPermissionModal(to);
            return false;
          }

          // is any active form dirty
          const dirtyForm = Object.entries(forms).find(
            ([_formId, formState]) => formState.isDirty === true
          );

          // check if there is any dirty form
          if (!skipDirtyForm && dirtyForm && dirtyForm[1].isDirty === true) {
            // prevent navigation if dirty form
            setDirtyForm(dirtyForm[1]);
            e.preventDefault();

            // stop onClick function frm triggering
            return false;
          }

          onClick && onClick(e);
          return true;
        }}
      >
        {children}
      </Link>
      {dirtyForm
        ? ReactDOM.createPortal(
            <UnsavedChangesModal
              dirtyForm={dirtyForm}
              setDirtyForm={setDirtyForm}
              triggerNavigation={triggerNavigation}
            />,
            document.body
          )
        : null}
    </>
  );
};
